import React from 'react';
import { Box, Flex, Paper, SimpleGrid, Text } from '@mantine/core';
import type { TFunction } from 'i18next';
import { Eyebrow, NumberMono } from '../../parts/primitives';

interface StatTileProps {
  label: string;
  value: number | string;
  sub: string;
  tone: 'accent' | 'warn' | 'danger' | 'neutral';
}

const dotColor = (tone: StatTileProps['tone']) => {
  switch (tone) {
    case 'accent':
      return 'var(--mantine-primary-color-filled)';
    case 'warn':
      return 'var(--mantine-color-orange-6)';
    case 'danger':
      return 'var(--mantine-color-red-6)';
    default:
      return 'var(--mantine-color-dimmed)';
  }
};

const StatTile: React.FC<StatTileProps> = ({ label, value, sub, tone }) => (
  <Paper withBorder p="md" radius="md">
    <Flex justify="space-between" align="center" mb="sm">
      <Eyebrow>{label}</Eyebrow>
      <Box
        style={{
          width: 7,
          height: 7,
          borderRadius: 999,
          background: dotColor(tone),
          flexShrink: 0,
        }}
      />
    </Flex>
    <NumberMono style={{ fontSize: 30, fontWeight: 700, letterSpacing: '-0.02em', lineHeight: 1 }}>
      {value}
    </NumberMono>
    <Text size="xs" c="dimmed" mt={6}>
      {sub}
    </Text>
  </Paper>
);

export interface AdminStatsData {
  activeRounds: number;
  drafts: number;
  closed: number;
  total: number;
}

interface AdminStatsProps {
  stats: AdminStatsData;
  t: TFunction;
}

export const AdminStats: React.FC<AdminStatsProps> = ({ stats, t }) => {
  const tiles: StatTileProps[] = [
    {
      label: t('perfReview.adminHome.stats.activeLabel'),
      value: stats.activeRounds,
      sub: t('perfReview.adminHome.stats.activeSub'),
      tone: 'accent',
    },
    {
      label: t('perfReview.adminHome.stats.draftsLabel'),
      value: stats.drafts,
      sub: t('perfReview.adminHome.stats.draftsSub'),
      tone: 'warn',
    },
    {
      label: t('perfReview.adminHome.stats.closedLabel'),
      value: stats.closed,
      sub: t('perfReview.adminHome.stats.closedSub'),
      tone: 'neutral',
    },
    {
      label: t('perfReview.adminHome.stats.totalLabel'),
      value: stats.total,
      sub: t('perfReview.adminHome.stats.totalSub'),
      tone: 'neutral',
    },
  ];
  return (
    <SimpleGrid cols={{ base: 2, md: 4 }} spacing="md">
      {tiles.map((tile) => (
        <StatTile key={tile.label} {...tile} />
      ))}
    </SimpleGrid>
  );
};
